import { useRef, useState } from "react";
import { Play, ArrowUpRight } from "lucide-react";
import { FadeUp, SectionLabel } from "./Reveal";
import { useLang } from "../lib/lang";

const TUTORIALS = [
  {
    id: "monkey",
    title: { es: "Personaje 3D en Illustrator", en: "3D character in Illustrator" },
    text: { es: "Del dibujo plano al personaje con volumen, paso a paso con las herramientas 3D.", en: "From a flat drawing to a character with volume, step by step with the 3D tools." },
    lang: "",
  },
  {
    id: "worm",
    title: { es: "Gusano de degradados", en: "Gradient worm" },
    text: { es: "Cómo crear un trazo 3D con degradados vibrantes y fusiones de color.", en: "How to build a 3D stroke with vibrant gradients and colour blends." },
    lang: "EN",
  },
  {
    id: "flowers",
    title: { es: "Flores en Illustrator", en: "Flowers in Illustrator" },
    text: { es: "De una simple estrella a flores pastel llenas de textura, con herramientas de distorsión.", en: "From a simple star to textured pastel flowers, using distortion tools." },
    lang: "",
  },
];

const TutorialCard = ({ t, i }) => {
  const { tr } = useLang();
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const start = () => {
    setStarted(true);
    ref.current?.play().catch(() => {});
  };
  return (
    <FadeUp delay={i * 0.1} className="group">
      <div className="relative aspect-[9/16] overflow-hidden rounded-3xl bg-black ring-1 ring-white/10">
        <video
          ref={ref}
          data-testid={`tutorial-video-${t.id}`}
          src={`/video/tut_${t.id}.mp4`}
          poster={`/video/tut_${t.id}.jpg`}
          controls={started}
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
        />
        {!started && (
          <button data-testid={`tutorial-play-${t.id}`} onClick={start} className="absolute inset-0 grid place-items-center bg-gradient-to-t from-black/70 via-black/10 to-transparent" aria-label="Play">
            <span className="grid h-16 w-16 place-items-center rounded-full bg-[var(--acid)] text-[var(--ink)] shadow-[0_10px_40px_rgba(242,138,192,0.5)] transition-transform duration-500 group-hover:scale-110">
              <Play size={28} fill="currentColor" />
            </span>
            <span className="absolute left-5 top-5 rounded-full bg-black/60 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-white backdrop-blur">Illustrator{t.lang && ` · ${t.lang}`}</span>
          </button>
        )}
      </div>
      <h3 className="mt-5 font-display text-xl font-medium">{tr(t.title)}</h3>
      <p className="mt-2 text-sm text-zinc-400">{tr(t.text)}</p>
    </FadeUp>
  );
};

const COURSE_URL = "https://www.domestika.org/es/courses/1833-creacion-de-terrarios-con-la-tecnica-tiffany";

const DomestikaCard = () => {
  const { tr } = useLang();
  const stats = [
    tr({ es: "2.423 estudiantes", en: "2,423 students" }),
    tr({ es: "100% valoraciones positivas", en: "100% positive reviews" }),
    tr({ es: "11 lecciones · 1h 16m", en: "11 lessons · 1h 16m" }),
    tr({ es: "Nivel: iniciación", en: "Level: beginner" }),
  ];
  return (
    <FadeUp className="mt-20">
      <a data-testid="domestika-course-link" href={COURSE_URL} target="_blank" rel="noopener noreferrer" className="group grid overflow-hidden rounded-[28px] bg-[var(--surface)] ring-1 ring-white/10 transition-colors hover:ring-[var(--acid)] lg:grid-cols-2">
        <div className="relative aspect-[1200/628] overflow-hidden lg:aspect-auto">
          <img src="/work/domestika.jpg" alt="Creación de terrarios con la técnica Tiffany — Domestika" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
        </div>
        <div className="flex flex-col justify-center p-8 sm:p-12">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--acid)]">{tr({ es: "Curso online · Domestika · Páramo Store", en: "Online course · Domestika · Páramo Store" })}</p>
          <h3 className="mt-4 font-display text-3xl font-medium leading-tight sm:text-4xl">{tr({ es: "Creación de terrarios con la técnica Tiffany", en: "Creating terrariums with the Tiffany technique" })}</h3>
          <p className="mt-4 text-zinc-300">{tr({ es: "Aprende a fabricar bellos contenedores de vidrio con formas geométricas para tus plantas favoritas.", en: "Learn to make beautiful geometric glass containers for your favourite plants." })}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {stats.map((s) => <span key={s} className="rounded-full border border-white/15 px-3 py-1 text-xs text-zinc-300">{s}</span>)}
          </div>
          <span className="mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-[var(--acid)] px-6 py-3 text-sm font-semibold text-[var(--ink)] transition-transform group-hover:scale-[1.03]">
            {tr({ es: "Ver curso en Domestika", en: "View course on Domestika" })} <ArrowUpRight size={16} />
          </span>
        </div>
      </a>
    </FadeUp>
  );
};

export const Tutorials = () => {
  const { tr } = useLang();
  return (
    <section id="tutorials" data-testid="tutorials-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <SectionLabel index="03">{tr({ es: "Tutoriales", en: "Tutorials" })}</SectionLabel>
          <FadeUp as="h2" className="mt-6 font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tight sm:text-6xl">
            {tr({ es: "Aprende", en: "Learn" })} <span className="font-serif-i normal-case text-[var(--acid)]">{tr({ es: "conmigo.", en: "with me." })}</span>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-6 max-w-sm text-zinc-300">
            {tr({ es: "Profesora en Domestika y creadora de tutoriales cortos de Illustrator: técnicas reales, explicadas paso a paso en pocos minutos.", en: "Domestika teacher and creator of short Illustrator tutorials: real techniques, explained step by step in a few minutes." })}
          </FadeUp>
        </div>
        <div className="grid gap-8 sm:grid-cols-3 lg:col-span-8">
          {TUTORIALS.map((t, i) => <TutorialCard key={t.id} t={t} i={i} />)}
        </div>
      </div>
      <DomestikaCard />
    </section>
  );
};
